import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Lock, X } from 'lucide-react';
import { formatDate, formatEuro, formatInteger, type UnitLinkRecord } from '@brimo/shared';
import { Button, Icon, IconButton, Select, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { linkUnit, unlinkUnit, usePropertyUnitOptions } from '../api';
import { availabilityPill } from '../availability';
import { CardHead, FieldPair, StatusPill } from '../bits';
import type { UnitDetail } from '../types';
import {
  amenityGroups,
  compositionRows,
  compositionShares,
  energyRows,
  hasAmenitiesOrEnergy,
  hasComposition,
} from './unitData';
import styles from './cards.module.css';

/** „Ausstattung & Energie" (hidden without any value): amenity chips per group, energy certificate, carriers, heating, KfW. */
export function AmenitiesEnergyCard({ unit }: { unit: UnitDetail }) {
  if (!hasAmenitiesOrEnergy(unit.fields)) return null;
  const groups = amenityGroups(unit.fields.amenities);
  return (
    <section className={styles.card} aria-labelledby="unit-amenities">
      <CardHead eyebrow="Ausstattung & Energie" id="unit-amenities" />
      {groups.length === 0 ? (
        <p className={styles.empty}>Keine Ausstattungsmerkmale hinterlegt.</p>
      ) : (
        <div className={styles.groups}>
          {groups.map((group) => (
            <div key={group.group}>
              <div className={styles.groupLabel}>{group.label}</div>
              <ul className={styles.chips} aria-label={group.label}>
                {group.items.map((label) => (
                  <li key={label} className={styles.chip}>
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      <div className={styles.subhead}>Energie</div>
      <div className={styles.dataGrid}>
        {energyRows(unit.fields).map((row) => (
          <FieldPair key={row.label} label={row.label} value={row.value} />
        ))}
      </div>
    </section>
  );
}

/** „Kaufpreisaufteilung" inside „Preis und Nebenkosten" (only when a part is set) with the derived shares. */
export function PriceComposition({ unit }: { unit: UnitDetail }) {
  if (!hasComposition(unit.fields)) return null;
  const shares = compositionShares(unit.fields);
  return (
    <div className={styles.composition}>
      <div className={styles.subhead}>Kaufpreisaufteilung</div>
      <div className={styles.compositionGrid}>
        {compositionRows(unit.fields).map((row) => (
          <div key={row.label} className={styles.compositionRow}>
            <span className={styles.compositionLabel}>{row.label}</span>
            <span className={styles.compositionValue}>
              {row.cents === null ? '—' : formatEuro(row.cents)}
            </span>
          </div>
        ))}
      </div>
      {shares.length > 0 ? (
        <div className={styles.shares}>
          {shares.map((row) => (
            <span key={row.label}>
              <span className={styles.shareLabel}>{row.label}</span> {row.value}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function period(beginOn: string | null, endOn: string | null): string {
  if (!beginOn && !endOn) return '—';
  return `${beginOn ? formatDate(beginOn) : 'offen'} – ${endOn ? formatDate(endOn) : 'offen'}`;
}

/** „Externe Reservierungen" (only when TeamProQ reports any): status, Zeitraum, Position, Makler, Hinweis. */
export function ExternalReservationsCard({ unit }: { unit: UnitDetail }) {
  if (unit.externalReservations.length === 0) return null;
  return (
    <section className={styles.card} aria-labelledby="unit-external-reservations">
      <CardHead
        eyebrow="Externe Reservierungen"
        id="unit-external-reservations"
        end={unit.external ? `aus ${unit.external.connectionName}` : undefined}
      />
      <div className={styles.rows}>
        {unit.externalReservations.map((item) => (
          <div key={item.id} className={styles.row}>
            <div className={styles.rowMain}>
              <span className={styles.rowLabel}>{item.statusLabel}</span>
              <span className={styles.rowMeta}>
                {[
                  period(item.beginOn, item.endOn),
                  item.position !== null ? `Position ${formatInteger(item.position)}` : null,
                  item.broker,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {item.notes ? <span className={styles.rowMeta}>{item.notes}</span> : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** „Verknüpfte Einheiten": links to the unit records, add/remove within the same property. */
export function LinkedUnitsCard({
  unit,
  editable,
  onChanged,
}: {
  unit: UnitDetail;
  editable: boolean;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [choice, setChoice] = useState('');
  const [pending, setPending] = useState(false);
  const importOnly = unit.external?.mode === 'import_only';
  const options = usePropertyUnitOptions(unit.propertyId, adding);
  const linked = useMemo(() => new Set(unit.links.map((link) => link.relatedUnitId)), [unit.links]);
  const candidates = (options.data?.items ?? []).filter((row) => row.id !== unit.id && !linked.has(row.id));

  if (unit.links.length === 0 && !editable) return null;

  async function add() {
    if (!choice) return;
    setPending(true);
    try {
      await linkUnit(unit.id, choice);
      setChoice('');
      setAdding(false);
      toast({ message: 'Einheit verknüpft.' });
      onChanged();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setPending(false);
    }
  }

  async function remove(link: UnitLinkRecord) {
    try {
      await unlinkUnit(unit.id, link.relatedUnitId);
      toast({ message: `Verknüpfung mit ${link.number} entfernt.` });
      onChanged();
    } catch (error) {
      toast({ message: errorMessage(error) });
    }
  }

  return (
    <section className={styles.card} aria-labelledby="unit-links">
      <CardHead
        eyebrow="Verknüpfte Einheiten"
        id="unit-links"
        end={unit.links.length > 0 ? 'werden gemeinsam verkauft' : undefined}
      />
      {unit.links.length === 0 ? (
        <p className={styles.empty}>Keine verknüpften Einheiten.</p>
      ) : (
        <div className={styles.rows}>
          {unit.links.map((link) => {
            const pill = availabilityPill(link.availability);
            const locked = link.imported && importOnly;
            return (
              <div key={link.relatedUnitId} className={styles.row}>
                <div className={styles.rowMain}>
                  <Link
                    className={styles.link}
                    to={`/properties/${unit.propertyId}/units/${link.relatedUnitId}`}
                  >
                    {link.number}
                  </Link>
                  <span className={styles.rowMeta}>{link.typeLabel}</span>
                </div>
                <StatusPill tone={pill.tone} unit>
                  {link.availabilityLabel}
                </StatusPill>
                {editable ? (
                  <div className={styles.rowActions}>
                    {locked ? (
                      <span className={styles.lock} title="Wird aus TeamProQ übernommen">
                        <Icon icon={Lock} size={16} />
                      </span>
                    ) : (
                      <IconButton
                        icon={X}
                        size={28}
                        variant="ghost"
                        label={`Verknüpfung mit ${link.number} entfernen`}
                        onClick={() => void remove(link)}
                      />
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {editable ? (
        adding ? (
          <div className={styles.addRow}>
            <Select
              label="Einheit"
              value={choice}
              placeholder={options.isPending ? 'Einheiten werden geladen …' : 'Bitte wählen'}
              options={candidates.map((row) => ({
                value: row.id,
                label: `${row.number} · ${row.typeLabel}`,
              }))}
              onChange={(event) => setChoice(event.target.value)}
            />
            <Button variant="secondary" onClick={() => setAdding(false)} disabled={pending}>
              Abbrechen
            </Button>
            <Button loading={pending} disabled={!choice} onClick={() => void add()}>
              Verknüpfen
            </Button>
          </div>
        ) : (
          <div className={styles.addAction}>
            <Button variant="secondary" size="small" onClick={() => setAdding(true)}>
              Einheit verknüpfen
            </Button>
          </div>
        )
      ) : null}
    </section>
  );
}
